/**
 * As mensagens pré-definidas da revenda (H05b).
 *
 * ── Duas coisas com o mesmo nome na tela, e por quê ──────────────────────
 *
 * O atendimento chama de "mensagens prontas" tanto isto quanto o modelo
 * aprovado da Meta, e a caixa que abre no chat mostra as duas em abas. Não é
 * descuido de vocabulário: para quem atende, as duas respondem à mesma
 * pergunta — "o que eu mando agora?" — e separá-las por nome obrigaria a
 * decorar de qual das duas famílias vem cada texto.
 *
 * O que as separa é o momento, e a aba diz isso: esta é texto livre, entregue
 * só DENTRO da janela de 24 h; a da Meta é o que sai fora dela.
 *
 * ── Sem variáveis, de propósito ──────────────────────────────────────────
 *
 * A mensagem entra no campo de texto e a pessoa edita antes de enviar — trocar
 * o nome do cliente é digitar por cima. Um sistema de variáveis aqui
 * acrescentaria um formulário entre escolher e enviar, para resolver o que a
 * edição já resolve, e criaria a chance de mandar `{{1}}` para o cliente.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

/**
 * Curto porque é rótulo de lista, não resumo do texto.
 *
 * Exportado com o do texto para o `maxLength` da tela sair daqui (#1192): uma
 * cópia na web truncaria o que a API aceita, ou deixaria passar o que ela
 * recusa, no dia em que um dos dois mudasse.
 */
export const TITULO_MAXIMO_DA_MENSAGEM_PRONTA = 80;
const TITULO_MAXIMO = TITULO_MAXIMO_DA_MENSAGEM_PRONTA;

/**
 * O teto do texto.
 *
 * Acima disto não é mais mensagem pronta: é documento, e documento se manda
 * como anexo. O limite do WhatsApp para uma mensagem de texto é bem maior, e
 * ainda assim ninguém lê 4.000 caracteres numa bolha.
 */
export const TEXTO_MAXIMO_DA_MENSAGEM_PRONTA = 2_000;
const TEXTO_MAXIMO = TEXTO_MAXIMO_DA_MENSAGEM_PRONTA;

export const MensagemProntaSchema = z.object({
  id: IdSchema,
  titulo: z.string(),
  texto: z.string(),
  criada_em: InstanteSchema,
  atualizada_em: InstanteSchema,
});

export type MensagemPronta = z.infer<typeof MensagemProntaSchema>;

export const MensagensProntasSchema = z.object({
  itens: z.array(MensagemProntaSchema),
});

export type MensagensProntas = z.infer<typeof MensagensProntasSchema>;

export const CriarMensagemProntaSchema = z
  .object({
    titulo: z.string().trim().min(1).max(TITULO_MAXIMO),
    texto: z.string().trim().min(1).max(TEXTO_MAXIMO),
  })
  .strict();

export type CriarMensagemPronta = z.infer<typeof CriarMensagemProntaSchema>;

/**
 * Editar, com os dois campos opcionais e pelo menos um presente.
 *
 * Campo ausente é "não mexer", como no cadastro do cliente. A recusa do corpo
 * vazio existe porque um `PATCH {}` que responde 200 sugere que algo mudou.
 */
export const EditarMensagemProntaSchema = z
  .object({
    titulo: z.string().trim().min(1).max(TITULO_MAXIMO).optional(),
    texto: z.string().trim().min(1).max(TEXTO_MAXIMO).optional(),
  })
  .strict()
  .refine((corpo) => Object.keys(corpo).length > 0, {
    message: 'Diga o que mudar: corpo vazio não descreve nenhuma mudança.',
  });

export type EditarMensagemPronta = z.infer<typeof EditarMensagemProntaSchema>;
